import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons, FUNDING } from "@paypal/react-paypal-js";
import {
  ArrowLeft, ArrowRight, BadgeCheck, Car, Check, Loader2,
  MapPin, PackageCheck, ShieldAlert, ShieldCheck, Lock,
} from "lucide-react";
import { api } from "@/lib/api";
import { findVehicle, CATEGORY_LABELS } from "@/lib/vehicles";
import { useVehicleState } from "@/lib/vehicleStorage";
import { VehicleSelector } from "@/components/VehicleSelector";
import { MaskedLines } from "@/components/MaskedLines";
import { Seo } from "@/components/Seo";
import { useLang } from "@/i18n";
import { fmtPrice } from "@/lib/format";
import { PROVINCES, computeTaxes } from "@/lib/taxes";

const inputCls =
  "w-full rounded-xl border border-slate-700/80 bg-slate-900/70 px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none transition-colors focus:border-sky-400/70 focus:ring-2 focus:ring-sky-500/20";

const PRICE = 276.8;

// Ordre demandé : carte de crédit, Interac, PayPal, Apple Pay
// `online: true` = traité via PayPal (activé quand les clés sont configurées)
const METHODS = [
  { id: "card", titleKey: "methodCard", subKey: "methodCardSub", online: true },
  { id: "interac", titleKey: "methodInterac", subKey: "methodInteracSub", online: false },
  { id: "paypal", titleKey: "methodPaypal", subKey: "methodPaypalSub", online: true },
  { id: "applepay", titleKey: "methodApplePay", subKey: "methodApplePaySub", online: true },
];

const FUNDING_FOR = { card: FUNDING.CARD, paypal: FUNDING.PAYPAL, applepay: FUNDING.APPLEPAY };

export default function Checkout() {
  const { t, lang } = useLang();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [storedVehicle, setStoredVehicle] = useVehicleState();
  // Priorité aux paramètres d'URL (lien « Commander » de la fiche produit), sinon véhicule mémorisé
  const [vehicle, setVehicle] = useState(() => {
    if (params.get("marque") || params.get("modele")) {
      return {
        marque: params.get("marque") || "",
        modele: params.get("modele") || "",
        annee: params.get("annee") || "",
      };
    }
    return storedVehicle;
  });
  const updateVehicle = (v) => {
    setVehicle(v);
    setStoredVehicle(v);
  };
  // Véhicule déjà connu : on saute l'étape de sélection
  const [step, setStep] = useState(() =>
    (params.get("marque") && params.get("modele")) || (storedVehicle.marque && storedVehicle.modele) ? 2 : 1
  );
  const [form, setForm] = useState({ name: "", email: "", phone: "", street: "", apartment: "", city: "", province: "", postalCode: "", quantity: 1 });
  const [paying, setPaying] = useState(false);

  const chosen = findVehicle(vehicle.marque, vehicle.modele);
  const vehicleLabel = chosen
    ? `${vehicle.marque} ${vehicle.modele}${vehicle.annee ? ` ${vehicle.annee}` : ""}`
    : "";

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const postalValid = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/.test(form.postalCode.trim());
  const step2Valid = form.name && form.email && form.phone && form.street && form.city && form.province && postalValid;
  const subtotal = PRICE * Number(form.quantity);
  const { total: taxTotal } = computeTaxes(subtotal, form.province, lang);
  const grandTotal = subtotal + taxTotal;

  // Modes de paiement disponibles (config serveur : PayPal/cartes activés seulement si clés présentes)
  const [payConfig, setPayConfig] = useState({ interac: true, paypal: false, paypal_client_id: "" });
  const [method, setMethod] = useState("interac");
  const methodTouched = useRef(false);
  const [pendingOrder, setPendingOrder] = useState(null);

  useEffect(() => {
    api.get("/payments/config").then((r) => {
      setPayConfig(r.data);
      if (r.data.paypal && !methodTouched.current) setMethod("card");
    }).catch(() => {});
  }, []);

  const chooseMethod = (id) => {
    methodTouched.current = true;
    setMethod(id);
  };

  const submitOrder = async () => {
    setPaying(true);
    try {
      const r = await api.post("/orders", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        vehicle: vehicleLabel,
        address: form.street,
        apartment: form.apartment,
        city: form.city,
        province: form.province,
        postal_code: form.postalCode.trim().toUpperCase(),
        quantity: Number(form.quantity),
        lang,
        payment_method: method,
      });
      if (method !== "interac") {
        setPendingOrder(r.data);
      } else {
        navigate(`/paiement/interac?order_id=${r.data.order_id}&code=${r.data.confirmation_code}`);
      }
    } catch {
      toast.error(t("checkout.errPay"));
    } finally {
      setPaying(false);
    }
  };

  const StepDot = ({ n, label }) => (
    <div className="flex items-center gap-2.5">
      <span
        className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold transition-all duration-300 ${
          step >= n ? "bg-sky-400 text-slate-950 shadow-[0_0_16px_rgba(56,189,248,0.4)]" : "border border-slate-700 text-slate-500"
        }`}
      >
        {step > n ? <Check size={14} /> : n}
      </span>
      <span className={`text-xs font-semibold sm:text-sm ${step >= n ? "text-slate-100" : "text-slate-500"}`}>{label}</span>
    </div>
  );

  return (
    <main className="mx-auto max-w-5xl px-4 pt-32 pb-24 sm:px-8">
      <Seo title={`${t("checkout.eyebrow")} | NordiGuard`} description={t("products.sub")} />

      <header className="pt-8">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sky-400">{t("checkout.eyebrow")}</p>
        <h1 className="font-display mt-3 text-4xl font-extrabold tracking-tight text-slate-50 sm:text-5xl">
          <MaskedLines lines={[t("checkout.l1"), t("checkout.l2")]} />
        </h1>
        <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3" data-testid="checkout-steps">
          {t("checkout.steps").map((label, i) => (
            <StepDot key={label} n={i + 1} label={label} />
          ))}
        </div>
      </header>

      {/* ÉTAPE 1 — VÉHICULE */}
      {step === 1 && (
        <motion.section
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="glacial-card mt-10 p-7 sm:p-9" data-testid="checkout-step-vehicle"
        >
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-sky-500/25 bg-sky-500/10 text-sky-400">
              <Car size={19} />
            </span>
            <div>
              <h2 className="font-display text-lg font-bold text-slate-50">{t("checkout.vehTitle")}</h2>
              <p className="text-xs text-slate-500">{t("checkout.vehSub")}</p>
            </div>
          </div>
          <div className="mt-5">
            <VehicleSelector value={vehicle} onChange={updateVehicle} />
          </div>
          {chosen && (
            <p className="mt-4 flex items-start gap-2.5 rounded-xl border border-emerald-500/25 bg-emerald-500/10 p-4 text-sm text-emerald-200" data-testid="checkout-vehicle-ok">
              <BadgeCheck size={17} className="mt-0.5 shrink-0 text-emerald-400" />
              {t("products.bannerOk", { vehicle: vehicleLabel, category: CATEGORY_LABELS[chosen.category] })}
            </p>
          )}
          <div className="mt-6 flex justify-end">
            <button
              data-testid="checkout-continue-1"
              disabled={!chosen}
              onClick={() => setStep(2)}
              className="group inline-flex items-center gap-2 rounded-full bg-sky-400 px-7 py-3.5 text-sm font-bold text-slate-950 transition-all duration-300 hover:bg-sky-300 hover:shadow-[0_0_26px_rgba(56,189,248,0.45)] disabled:cursor-not-allowed disabled:opacity-40"
            >
              {t("checkout.continue")}
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </div>
        </motion.section>
      )}

      {/* ÉTAPE 2 — COORDONNÉES */}
      {step === 2 && (
        <motion.section
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="glacial-card mt-10 p-7 sm:p-9" data-testid="checkout-step-contact"
        >
          <h2 className="font-display text-lg font-bold text-slate-50">{t("checkout.contactTitle")}</h2>
          {chosen && (
            <div
              data-testid="checkout-vehicle-recap"
              className="mt-5 flex items-center justify-between gap-4 rounded-xl border border-sky-500/25 bg-sky-500/10 px-4 py-3"
            >
              <p className="flex items-center gap-2.5 text-sm font-semibold text-slate-100">
                <Car size={16} className="shrink-0 text-sky-400" />
                {vehicleLabel}
              </p>
              <button
                data-testid="checkout-change-vehicle"
                onClick={() => setStep(1)}
                className="shrink-0 text-xs font-bold text-sky-300 underline-offset-2 transition-colors hover:text-sky-200 hover:underline"
              >
                {t("checkout.changeVehicle")}
              </button>
            </div>
          )}
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <input data-testid="checkout-input-name" placeholder={t("checkout.name")} value={form.name} onChange={set("name")} className={inputCls} />
            <input data-testid="checkout-input-email" type="email" placeholder={t("checkout.email")} value={form.email} onChange={set("email")} className={inputCls} />
            <input data-testid="checkout-input-phone" type="tel" placeholder={t("checkout.phone")} value={form.phone} onChange={set("phone")} className={inputCls} />
            <select data-testid="checkout-select-quantity" value={form.quantity} onChange={set("quantity")} className={inputCls}>
              {[1, 2, 3, 4].map((q) => (
                <option key={q} value={q}>{t("checkout.qty")} : {q}</option>
              ))}
            </select>
          </div>

          <h3 className="mt-8 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-sky-400">
            <MapPin size={13} /> {t("checkout.address")}
          </h3>
          <div className="mt-4 grid gap-4">
            <input
              data-testid="checkout-input-address"
              placeholder={t("checkout.street")}
              value={form.street}
              onChange={set("street")}
              className={inputCls}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <input
                data-testid="checkout-input-apartment"
                placeholder={t("checkout.apartment")}
                value={form.apartment}
                onChange={set("apartment")}
                className={inputCls}
              />
              <input
                data-testid="checkout-input-city"
                placeholder={t("checkout.city")}
                value={form.city}
                onChange={set("city")}
                className={inputCls}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <select
                data-testid="checkout-select-province"
                value={form.province}
                onChange={set("province")}
                className={`${inputCls} ${form.province ? "" : "text-slate-500"}`}
              >
                <option value="" disabled>{t("checkout.provincePlaceholder")}</option>
                {PROVINCES.map((p) => (
                  <option key={p.code} value={p.code}>{lang === "en" ? p.en : p.fr}</option>
                ))}
              </select>
              <div>
                <input
                  data-testid="checkout-input-postal"
                  placeholder={t("checkout.postalCode")}
                  value={form.postalCode}
                  onChange={set("postalCode")}
                  maxLength={7}
                  className={`${inputCls} uppercase ${form.postalCode && !postalValid ? "border-red-400/60" : ""}`}
                />
                <p className={`mt-1.5 text-[11px] ${form.postalCode && !postalValid ? "text-red-400" : "text-slate-500"}`} data-testid="checkout-postal-hint">
                  {form.postalCode && !postalValid ? t("checkout.postalInvalid") : t("checkout.postalHint")}
                </p>
              </div>
            </div>
          </div>
          <div className="mt-6 flex justify-between">
            <button data-testid="checkout-back-2" onClick={() => setStep(1)} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-400 transition-colors hover:text-sky-300">
              <ArrowLeft size={15} /> {t("checkout.back")}
            </button>
            <button
              data-testid="checkout-continue-2"
              disabled={!step2Valid}
              onClick={() => setStep(3)}
              className="group inline-flex items-center gap-2 rounded-full bg-sky-400 px-7 py-3.5 text-sm font-bold text-slate-950 transition-all duration-300 hover:bg-sky-300 hover:shadow-[0_0_26px_rgba(56,189,248,0.45)] disabled:cursor-not-allowed disabled:opacity-40"
            >
              {t("checkout.continue")}
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </div>
        </motion.section>
      )}

      {/* ÉTAPE 3 — RÉVISION & PAIEMENT */}
      {step === 3 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 grid gap-6 lg:grid-cols-5" data-testid="checkout-step-review"
        >
          <div className="space-y-6 lg:col-span-3">
            <div className="glacial-card p-7">
              <h2 className="font-display flex items-center gap-2.5 text-lg font-bold text-slate-50">
                <PackageCheck size={19} className="text-sky-400" /> {t("checkout.receiveTitle")}
              </h2>
              <ul className="mt-4 space-y-3">
                {t("checkout.receive").map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-slate-300">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sky-500/15 text-sky-400">
                      <Check size={12} />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="glacial-card border-amber-500/20 p-7">
              <h2 className="font-display flex items-center gap-2.5 text-lg font-bold text-slate-50">
                <ShieldAlert size={19} className="text-amber-400" /> {t("checkout.safetyTitle")}
              </h2>
              <ul className="mt-4 space-y-3">
                {t("checkout.safety").map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-slate-400">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="glacial-card sticky top-24 p-7">
              <h2 className="font-display text-lg font-bold text-slate-50">{t("checkout.summaryTitle")}</h2>
              <div className="mt-4 flex gap-4">
                <img src="/images/sieges-avant.png" alt="NordiGuard Sur-Mesure 3D" className="h-20 w-24 rounded-lg object-cover" />
                <div className="text-sm">
                  <p className="font-semibold text-slate-100">NordiGuard Sur-Mesure 3D</p>
                  <p className="mt-0.5 text-xs text-slate-400">{vehicleLabel}</p>
                  <p className="mt-0.5 text-xs text-slate-500">{t("checkout.qty")} : {form.quantity}</p>
                </div>
              </div>
              <dl className="mt-5 space-y-2.5 border-t border-slate-800 pt-5 text-sm">
                <div className="flex justify-between">
                  <dt className="text-slate-400">Sur-Mesure 3D × {form.quantity}</dt>
                  <dd className="font-semibold text-slate-100">{fmtPrice(subtotal, lang)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-slate-400">{t("checkout.shipping")}</dt>
                  <dd className="font-semibold text-emerald-400">{t("checkout.free")}</dd>
                </div>
                {taxTotal > 0 && (
                  <div className="flex justify-between" data-testid="checkout-tax-combined">
                    <dt className="text-slate-400">{t("checkout.taxes")}</dt>
                    <dd className="font-semibold text-slate-100">{fmtPrice(taxTotal, lang)}</dd>
                  </div>
                )}
              </dl>
              <div className="mt-4 flex items-baseline justify-between border-t border-slate-800 pt-4">
                <span className="text-sm font-semibold text-slate-300">{t("checkout.total")}</span>
                <span className="font-display text-2xl font-extrabold text-slate-50" data-testid="checkout-grand-total">{fmtPrice(grandTotal, lang)}</span>
              </div>
              <p className="mt-1 text-right text-xs text-slate-500">{t("checkout.taxNote")}</p>

              {/* Choix du mode de paiement */}
              <div className="mt-6 border-t border-slate-800 pt-5">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">{t("checkout.methodTitle")}</p>
                <div className="mt-3 grid gap-3">
                  {METHODS.map(({ id, titleKey, subKey, online }) => {
                    const enabled = !online || payConfig.paypal;
                    return (
                      <button
                        key={id}
                        type="button"
                        data-testid={`checkout-method-${id}`}
                        onClick={() => enabled && chooseMethod(id)}
                        disabled={!enabled}
                        className={`flex items-start gap-3.5 rounded-xl border p-4 text-left transition-all duration-300 ${
                          !enabled
                            ? "cursor-not-allowed border-slate-800/60 bg-slate-900/20 opacity-50"
                            : method === id
                              ? "border-sky-400/60 bg-sky-500/10 shadow-[0_0_20px_rgba(56,189,248,0.15)]"
                              : "border-slate-800 bg-slate-900/40 hover:border-slate-700"
                        }`}
                      >
                        <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                          method === id ? "border-sky-400 bg-sky-400" : "border-slate-600"
                        }`}>
                          {method === id && <Check size={12} strokeWidth={3.5} className="text-slate-950" />}
                        </span>
                        <span className="flex-1">
                          <span className="flex items-center justify-between gap-2 text-sm font-bold text-slate-100">
                            {t(`checkout.${titleKey}`)}
                            {!enabled && (
                              <span className="rounded-full bg-slate-800 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                                {t("checkout.methodSoon")}
                              </span>
                            )}
                          </span>
                          <span className="mt-0.5 block text-xs leading-relaxed text-slate-400">{t(`checkout.${subKey}`)}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {!pendingOrder ? (
                <button
                  data-testid="checkout-pay-button"
                  onClick={submitOrder}
                  disabled={paying}
                  className="group mt-6 flex w-full items-center justify-center gap-2.5 rounded-full bg-sky-400 py-4 text-base font-extrabold text-slate-950 shadow-[0_0_24px_rgba(56,189,248,0.35)] transition-all duration-300 hover:scale-[1.02] hover:bg-sky-300 hover:shadow-[0_0_40px_rgba(56,189,248,0.55)] disabled:opacity-60"
                >
                  {paying ? <Loader2 size={18} className="animate-spin" /> : <Lock size={17} />}
                  {paying ? t("checkout.processing") : `${t("checkout.payBtn")} · ${fmtPrice(grandTotal, lang)}`}
                  {!paying && <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />}
                </button>
              ) : (
                <div className="mt-6" data-testid="checkout-paypal-buttons">
                  <p className="mb-3 text-center text-xs text-slate-400">{t("checkout.paypalHint")}</p>
                  <PayPalScriptProvider options={{ "client-id": payConfig.paypal_client_id, currency: "CAD", "enable-funding": "card", locale: lang === "en" ? "en_CA" : "fr_CA" }}>
                    <PayPalButtons
                      fundingSource={FUNDING_FOR[method] || FUNDING.PAYPAL}
                      style={{ layout: "vertical", color: method === "paypal" ? "gold" : "black", shape: "pill", label: "pay" }}
                      createOrder={async () => {
                        const r = await api.post("/paypal/create", {
                          order_id: pendingOrder.order_id,
                          code: pendingOrder.confirmation_code,
                        });
                        return r.data.paypal_order_id;
                      }}
                      onApprove={async (data) => {
                        const r = await api.post("/paypal/capture", { paypal_order_id: data.orderID });
                        navigate(`/paiement/succes?order_id=${r.data.order_id}&code=${r.data.confirmation_code}`);
                      }}
                      onError={() => toast.error(t("checkout.errPay"))}
                    />
                  </PayPalScriptProvider>
                </div>
              )}
              <p className="mt-3 flex items-center justify-center gap-1.5 text-center text-xs text-slate-500">
                <ShieldCheck size={13} className="text-sky-400" /> {t("checkout.secureNote")}
              </p>
              <button data-testid="checkout-back-3" onClick={() => setStep(2)} className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-slate-500 transition-colors hover:text-sky-300">
                <ArrowLeft size={13} /> {t("checkout.back")}
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {!chosen && step === 1 && (
        <p className="mt-4 text-center text-xs text-slate-500">
          <Link to="/produits" className="text-sky-400 underline underline-offset-2">{t("detail.backCatalog")}</Link>
        </p>
      )}
    </main>
  );
}
